import React from 'react';
import { ConfigProvider } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { useTheme } from '../../theme/ThemeContext';
import { fixUnclosedColorSpans, agenticMarkdownComponents } from '../../shared/markdownComponents';
import { markdownSanitizeSchema } from '../../shared/markdownSanitizeSchema';
import AiSparkle from '../../atoms/Icon/AiSparkle';

export type FindingTextType = 'regular' | 'bold' | 'label' | 'positive' | 'negative' | 'orange';

export interface FindingTextSegment {
    /** The text content */
    text: string;
    /** Type of text styling */
    type?: FindingTextType;
}

/** One task a finding cites, rendered as a numbered badge after the finding text. */
export interface TakeawaySource {
    /** task_id to navigate to - matches `data-task-id` on the right panel artefact card */
    taskId: string;
    /** 1-based task serial within the answer, shown inside the badge */
    serial: number;
    /** False when the task produced no artefact card, so the badge renders without a click target */
    hasArtefact?: boolean;
}

export interface FindingItem {
    /** Array of text segments with formatting */
    segments: FindingTextSegment[];
    /** Tasks this finding was derived from, rendered as numbered citation badges */
    sourceTasks?: TakeawaySource[];
}

export interface KeyFindingsProps {
    /** Title of the findings section */
    title?: string;
    /** Array of finding items */
    items?: FindingItem[];
    /** When true, render item text as markdown (tables, headers, lists, etc.) - like AnalysisSteps */
    renderAsMarkdown?: boolean;
    /** Fired when a citation badge is clicked. Badges are inert when omitted. */
    onCitationClick?: (taskId: string, serial: number) => void;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

const CITATION_BADGE_CLASS =
    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-[4px] rounded-full border border-solid border-white bg-[#434751] font-['Open_Sans'] font-semibold text-[11px] leading-none text-white align-middle";

const KeyFindings: React.FC<KeyFindingsProps> = (props) => {
    const { items = [], renderAsMarkdown = false, onCitationClick } = props;

    const theme = useTheme();

    const getTextStyle = (type: FindingTextType = 'regular'): string => {
        switch (type) {
            case 'label':
                return 'text-[16px] leading-[24px] text-[#1f1f1f]';
            case 'bold':
                return 'text-[16px] leading-[24px] text-[#140b1d]';
            case 'positive':
                return 'text-[16px] leading-[24px] text-[#00a63e]';
            case 'negative':
                return 'text-[16px] leading-[24px] text-[#ec343e]';
            case 'orange':
                return 'text-[16px] leading-[24px] text-[#ff6b35]';
            case 'regular':
                return 'text-[16px] leading-[24px]';
            default:
                return 'text-[16px] leading-[24px] font-normal text-[#4a5565]';
        }
    };

    const renderFindingText = (segments: FindingTextSegment[]) => {
        return segments.map((segment, index) => (
            <span key={index} className={getTextStyle(segment.type)}>
                {segment.text}
            </span>
        ));
    };

    const renderCitations = (item: FindingItem, itemIndex: number) => {
        if (!item.sourceTasks || item.sourceTasks.length === 0) return null;
        const baseTestId = props['data-test-id'];
        return (
            <span className="inline-flex items-center gap-1 ml-1 align-middle" data-key-findings-citations>
                {item.sourceTasks.map((source, sourceIndex) => {
                    const key = `${source.taskId}-${sourceIndex}`;
                    const testId = baseTestId ? `${baseTestId}-citation-${itemIndex}-${source.serial}` : undefined;
                    if (source.hasArtefact === false || !onCitationClick) {
                        return (
                            <span
                                key={key}
                                className={CITATION_BADGE_CLASS}
                                data-citation-task-id={source.taskId}
                                data-test-id={testId}
                            >
                                {source.serial}
                            </span>
                        );
                    }
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onCitationClick(source.taskId, source.serial)}
                            className={`${CITATION_BADGE_CLASS} cursor-pointer transition-colors hover:bg-[#2f323a]`}
                            aria-label={`Go to task ${source.serial}`}
                            data-citation-task-id={source.taskId}
                            data-test-id={testId}
                        >
                            {source.serial}
                        </button>
                    );
                })}
            </span>
        );
    };

    const renderItemContent = (item: FindingItem, itemIndex: number) => {
        const citations = renderCitations(item, itemIndex);
        const text = fixUnclosedColorSpans(item.segments.map((s) => s.text).join(''));
        if (renderAsMarkdown && text) {
            return (
                // Citations render as a trailing sibling after the markdown, so the last
                // <p> is never :last-child - target :last-of-type so it still goes inline.
                <div
                    className={`font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] markdown-content${
                        citations ? ' [&>p:last-of-type]:inline' : ''
                    }`}
                >
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
                        remarkPlugins={[remarkGfm]}
                        remarkRehypeOptions={{ allowDangerousHtml: true }}
                        components={agenticMarkdownComponents}
                    >
                        {text}
                    </ReactMarkdown>
                    {citations}
                </div>
            );
        }
        return (
            <>
                {renderFindingText(item.segments)}
                {citations}
            </>
        );
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="relative flex items-start gap-1 p-2 rounded-lg w-full border border-[#e8effc] overflow-hidden"
                style={{
                    backgroundImage:
                        'radial-gradient(120% 160% at 8% 15%, rgba(243,240,255,1) 0%, rgba(255,255,255,1) 55%)',
                }}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-key-findings
            >
                {/* Title */}
                {/* <p className="font-sans font-semibold text-[16px] leading-[22px] text-[#1f1f1f] m-0">{title}</p> */}

                {/* Decorative gradient ellipses, matching Figma node 22057-95751 */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-16 -top-10 h-20 w-72 rounded-full"
                    style={{ backgroundColor: '#e8effc', filter: 'blur(35px)' }}
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-12 right-16 h-20 w-64 rounded-full"
                    style={{
                        backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(246,243,255,1) 100%)',
                        filter: 'blur(40px)',
                    }}
                />

                <AiSparkle size={16} className="relative z-10 shrink-0 mt-1" />

                {/* Finding Items */}
                <div className="relative z-10 flex items-start w-full">
                    {renderAsMarkdown ? (
                        <ul className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] w-full m-0 pl-5 list-disc space-y-2">
                            {items.map((item, index) => (
                                <li key={index} className="mb-0">
                                    {renderItemContent(item, index)}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="font-sans text-[16px] leading-[24px] m-0 pl-5 list-disc w-full">
                            {items.map((item, index) => (
                                <li key={index} className="mb-0">
                                    {renderItemContent(item, index)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default KeyFindings;
